/**
 * 放送地点マスタの一覧 + CRUD ページ。
 *
 * トップページ (線区未選択時):
 *   - 全件テーブルの代わりに `RouteLandingSelector` を表示する。
 *     実運用では「まず線区を選ぶ」のが最初の操作であり、全線区の一覧を眺める場面が無いため。
 *   - ランディングは **1 段階選択** で、線区カード内の方面ボタンを 1 クリックすると
 *     そのまま一覧 + 地図画面へ遷移する。
 *   - この状態では一覧 API (全件 scan) を呼ばない。初期表示が軽くなる。
 *
 * 全線区モード (線区ドロップダウンで「(全線区)」を選んだ状態):
 *   - `selectedRouteId === ALL_ROUTES` で表され、**未選択 (空文字) とは区別する**。
 *     以前は「(全線区)」の値が空文字だったため、選んだ瞬間に「未選択」と判定されて
 *     線区選択ランディングへ戻ってしまう不具合があった。
 *   - 一覧は routeDirection を指定せずに取得する (= 全件 scan)。
 *   - 地図も表示し、全線区の放送地点ピンを重ねて描画する。
 *     線形 (GeoJSON) は線区/方面が特定できないため描画しない。
 *
 * 線区選択後のレイアウト (画面全体を使う 2 カラム構成):
 *   - 左カラム
 *       1. 操作パネル: 線区/方面セレクタ (線区カラーのライン付き) + アクション
 *          (線区選択に戻る / 再読み込み / 新規作成 / Import/Export)
 *       2. 放送地点テーブル (残り領域をスクロール)
 *   - 右カラム: 地図 (ウィンドウ下端までフル高)
 *       - 線区が選択されているときだけ表示し、選択された路線を強調表示する。
 *       - 既存の放送地点をクリック可能なピンで描画し、クリックで編集モーダルを開く。
 *   - 2 カラムは `SplitPane` で分割し、境界バーのドラッグで配分を変更できる。
 *   - 線区未選択時は地図が無いため、左カラムのみを全幅表示する。
 *
 * 編集 / 新規作成は BroadcastPointForm モーダル内で行い、保存時には ControlPanel API
 * (POST/PUT/DELETE) を即座に呼び出して結果を再取得する。
 *
 * 選択中の線区 / 方面は `useRouteSelection` により URL クエリ + sessionStorage に
 * 保持されるため、画面をリロードしても選択状態が維持される。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiError, base64ToAudioUrl, BroadcastPointsClient } from '../api/broadcastPoints';
import type { BroadcastContentCategory, BroadcastPoint, Chime } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { ChimesClient } from '../api/chimes';
import type { AppConfig } from '../config';
import { useHomeNavigationGuard } from '../contexts/HomeNavigationGuard';
import type { GtfsStop } from '../data/gtfsStops';
import { fetchIStationsLines, type IStationsLine } from '../data/istationsLines';

import {
  fetchRoutesMaster,
  parseRouteDirection,
  toRouteDirection,
  type RouteMaster,
} from '../data/routes';
import { fetchRouteShapePoints, type RouteShapePointsFile } from '../data/routeShapes';
import { findStationSegment, sortStopsBySequence, type RouteStop } from '../data/routeStops';
import { ALL_ROUTES, sanitizeSelection, useRouteSelection } from '../hooks/useRouteSelection';
import { extractContents } from '../utils/broadcastContents';

import {
  BroadcastPointForm,
  type BroadcastPointFormValues,
} from '../components/BroadcastPointForm';
import { BroadcastPointsMap } from '../components/BroadcastPointsMap';
import { BroadcastPointsTable } from '../components/BroadcastPointsTable';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ImportExportControl } from '../components/ImportExportControl';
import { RouteLandingSelector } from '../components/RouteLandingSelector';
import { RouteSelector } from '../components/RouteSelector';
import { SplitPane } from '../components/SplitPane';

interface BroadcastPointsPageProps {
  config: AppConfig;
}

interface FormState {
  mode: 'create' | 'edit';
  initialValue: BroadcastPointFormValues;
}

const DEFAULT_RADIUS = 100;
const DEFAULT_BROADCAST_DELAY = 0;

type SortOrder = 'station_order' | 'created_at';
type ContentFilterValue = '' | BroadcastContentCategory;

export function BroadcastPointsPage(props: BroadcastPointsPageProps): React.JSX.Element {
  const { config } = props;
  const { getIdToken } = useAuth();

  const apiClient = useMemo(
    () => new BroadcastPointsClient({ getIdToken, config }),
    [getIdToken, config],
  );

  /** チャイム音マスタ (`/chimes`) API クライアント (要件 3・4) */
  const chimesClient = useMemo(
    () => new ChimesClient({ getIdToken, config }),
    [getIdToken, config],
  );

  const [routes, setRoutes] = useState<RouteMaster[]>([]);
  /**
   * 駅順マスタ (route_id → 駅一覧)。接続線遅延コンテンツの開始/終了駅ドロップダウン用。
   * routes 読み込み後に ControlPanel API (`GET /route-stops`) からまとめて事前取得する。
   */
  const [routeStops, setRouteStops] = useState<Record<string, RouteStop[]>>({});

  /**
   * アイステイションズ路線マスタ (`istations-lines.json`)。
   *
   * 接続線遅延コンテンツの「運行情報の参照元（アイステイションズ路線）」複数選択の
   * 選択肢として使う。ATOS 線区マスタ (`routes`) とは **独立した別系統** で、
   * 接続線の `route_id` (ATOS) と一致しない路線も選べる必要があるため連動させない。
   *
   * 取得失敗時は空配列のまま (= プルダウンに選択肢が出ない) とし、放送地点の
   * 編集・保存自体はブロックしない (routes-master と同様のフェイルセーフ)。
   */
  const [istationsLines, setIStationsLines] = useState<IStationsLine[]>([]);

  /**
   * チャイム音マスタ一覧 (要件 3・4)。
   * 放送地点フォームの「放送開始 / 終了チャイム」セレクタの選択肢として使う。
   * 取得失敗時は空配列のまま (= セレクタが「(なし)」のみ) とし、放送地点の
   * 編集・保存自体はブロックしない (チャイムはオプション機能のためフェイルセーフ)。
   */
  const [chimes, setChimes] = useState<Chime[]>([]);

  /**
   * GTFS 静的 stops (親駅 + 子番線) の全件。
   *
   * 任意テキスト放送の放送条件「終着番線」(`arrival_stop_id`) のドロップダウン候補として
   * 使う。`GET /gtfs-stops` は `stops.txt` に `route_id` カラムが無いため **route_id で
   * 絞り込めず**、`routeStops` のように route_id 別 Record を組み立てられない。
   * そのため線区選択に関係なく **マウント時に 1 回だけ全件取得** してキャッシュし、
   * フォームへそのまま渡す (番線候補への絞り込みは `findPlatformStopIds` が行う)。
   *
   * 取得失敗時は空配列のまま (= 番線セレクトが無効化されて注記が出るだけ) とし、
   * 放送地点の編集・保存自体はブロックしない
   * (`istationsLines` / `chimes` と同じフェイルセーフ方針)。
   */
  const [gtfsStops, setGtfsStops] = useState<GtfsStop[]>([]);

  /**
   * 選択中の方面に対応する線形 Point GeoJSON。
   * `null` (= GeoJSON 未配信) の時は地図側で線形を描画しない。
   */
  const [routeShapePoints, setRouteShapePoints] = useState<RouteShapePointsFile | null>(null);
  const [allPoints, setAllPoints] = useState<BroadcastPoint[]>([]);

  /**
   * 選択中の線区 / 方面。URL クエリ (`?route=...&direction=...`) と sessionStorage に
   * 同期されるため、画面リロードでも選択が保持される。
   */
  const { routeId, directionName, setSelection, clearSelection } = useRouteSelection();

  /**
   * マスタ検証済みの選択値。
   * URL に手打ちされた不正な線区/方面や、マスタから削除された線区を弾く。
   */
  const { routeId: selectedRouteId, directionName: selectedDirectionName } = useMemo(
    () => sanitizeSelection(routes, { routeId, directionName }),
    [routes, routeId, directionName],
  );

  /**
   * URL / sessionStorage に不正な値 (マスタに無い線区・方面) が入っていた場合は、
   * 検証済みの値で URL クエリを書き戻して整合させる。
   * (書き戻さないと、URL に不正値が残り続けてブックマーク時に再現してしまう)
   */
  useEffect(() => {
    if (routes.length === 0) return;
    if (routeId === selectedRouteId && directionName === selectedDirectionName) return;
    setSelection(selectedRouteId, selectedDirectionName);
  }, [routes, routeId, directionName, selectedRouteId, selectedDirectionName, setSelection]);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formState, setFormState] = useState<FormState | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('station_order');
  const [contentFilter, setContentFilter] = useState<ContentFilterValue>('');
  const [focusPoint, setFocusPoint] = useState<BroadcastPoint | null>(null);
  /**
   * 削除確認ダイアログの対象。null の場合はダイアログ非表示。
   * window.confirm の代わりにアプリ独自の ConfirmDialog を表示する。
   */
  const [deleteTarget, setDeleteTarget] = useState<BroadcastPoint | null>(null);

  /**
   * 「(全線区)」が選択されているか。
   * 未選択 (空文字) と違い、一覧 + 地図はそのまま表示し、線区での絞り込みだけを外す。
   */
  const isAllRoutes = selectedRouteId === ALL_ROUTES;

  /**
   * アイステイションズ路線マスタの初回取得。
   *
   * `routes-master.json` と同様に CloudFront 同居アセットを fetch するだけなので、
   * 線区選択の有無に関わらずマウント時に 1 回だけ取得する。
   * 取得に失敗しても画面全体のエラー表示はせず空配列のままにする
   * (接続線遅延の参照元設定は任意項目のため、他の操作をブロックしない)。
   */
  useEffect(() => {
    let aborted = false;
    void (async () => {
      try {
        const file = await fetchIStationsLines();
        if (aborted) return;
        setIStationsLines(Array.isArray(file.lines) ? file.lines : []);
      } catch (err) {
        // マスタ未配置 / 取得失敗時は選択肢なしで続行する (フェイルセーフ)。
        console.warn('Failed to load istations-lines.json', err);
      }
    })();
    return () => {
      aborted = true;
    };
  }, []);

  const selectedRouteDirection = useMemo(() => {
    // 全線区モードは特定の routeDirection を持たない (= 全件取得)
    if (isAllRoutes || !selectedRouteId || !selectedDirectionName) return '';
    return toRouteDirection(selectedRouteId, selectedDirectionName);
  }, [isAllRoutes, selectedRouteId, selectedDirectionName]);

  // 表示対象の絞り込み (テーブル + 地図用 otherPoints)
  const filteredPoints = useMemo(() => {
    // 全線区モードでは線区・方面の絞り込みを行わず、取得した全件をそのまま使う
    if (isAllRoutes) return allPoints;
    return allPoints.filter((p) => {
      if (selectedRouteId) {
        const { routeId } = parseRouteDirection(p.routeDirection);
        if (routeId !== selectedRouteId) return false;
      }
      if (selectedDirectionName) {
        const { directionName } = parseRouteDirection(p.routeDirection);
        if (directionName !== selectedDirectionName) return false;
      }
      return true;
    });
  }, [allPoints, isAllRoutes, selectedRouteId, selectedDirectionName]);

  /**
   * filteredPoints にコンテンツ種別フィルターとソートを適用した結果。
   * テーブル表示に使用する。地図は filteredPoints をそのまま使う。
   */
  const sortedFilteredPoints = useMemo(() => {
    // 1. コンテンツ種別フィルター
    const afterFilter = contentFilter
      ? filteredPoints.filter((p) => extractContents(p).some((c) => c.category === contentFilter))
      : filteredPoints;

    // 2. ソート
    // 駅順マスタは放送地点自身の線区 (routeDirection の線区部分) から引く。
    // 全線区モードでは複数線区が混在するため、選択中の線区固定では引けない。
    const stopsOf = (point: BroadcastPoint): RouteStop[] => {
      const { routeId: pointRouteId } = parseRouteDirection(point.routeDirection);
      return routeStops[pointRouteId] ?? [];
    };

    return afterFilter.slice().sort((a, b) => {
      if (sortOrder === 'station_order') {
        // 全線区モードでは、まず線区#方面ごとにまとめてから駅順に並べる
        // (異なる線区の駅順序を混ぜて比較しても意味が無いため)
        if (isAllRoutes && a.routeDirection !== b.routeDirection) {
          return a.routeDirection.localeCompare(b.routeDirection);
        }
        const seqA = getStationSequence(a, stopsOf(a));
        const seqB = getStationSequence(b, stopsOf(b));
        if (seqA !== seqB) return seqA - seqB;
        // 同順のときは routeDirection → spotId の辞書順で安定ソート
        if (a.routeDirection !== b.routeDirection)
          return a.routeDirection.localeCompare(b.routeDirection);
        return a.spotId.localeCompare(b.spotId);
      }
      // created_at: spotId (UUID v7) の辞書順 = 登録日時順
      return a.spotId.localeCompare(b.spotId);
    });
  }, [filteredPoints, contentFilter, sortOrder, isAllRoutes, routeStops]);

  // 初期データロード (routes-master.json のみ; 線形 GeoJSON は方面選択後に取得)
  useEffect(() => {
    let aborted = false;
    void (async () => {
      try {
        const rm = await fetchRoutesMaster();
        if (aborted) return;
        setRoutes(rm.routes);
      } catch (err) {
        if (!aborted) setErrorMessage((err as Error).message);
      }
    })();
    return () => {
      aborted = true;
    };
  }, []);

  /**
   * チャイム音マスタ (`GET /chimes`) を初回マウント時に取得する (要件 3・4)。
   *
   * 放送地点フォームのチャイムセレクタの選択肢として使う。取得失敗時は
   * 空配列のままにして画面の他機能に影響を与えない (チャイムはオプション機能)。
   */
  useEffect(() => {
    let aborted = false;
    void (async () => {
      try {
        const res = await chimesClient.list();
        if (!aborted) setChimes(res.items);
      } catch {
        // チャイムマスタの取得失敗は放送地点の編集を妨げないため無視する
        // (セレクタが「(なし)」のみになる)。
        if (!aborted) setChimes([]);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [chimesClient]);

  /**
   * GTFS 静的 stops (`GET /gtfs-stops`) を初回マウント時に 1 回だけ全件取得する。
   *
   * 放送条件「終着番線」の駅 / 番線ドロップダウンの候補ソース。`route_id` では
   * 絞り込めない (`stops.txt` に route_id カラムが無い) ため、`routeStops` のように
   * 路線ごとに引くのではなく全件を単発取得してキャッシュする。
   * 取得失敗時は空配列のままにして画面の他機能に影響を与えない
   * (番線セレクトが無効化され、設定済みの値は選択肢として残る)。
   */
  useEffect(() => {
    let aborted = false;
    void (async () => {
      try {
        const stops = await apiClient.getGtfsStops();
        if (!aborted) setGtfsStops(stops);
      } catch {
        // GTFS stops の取得失敗は放送地点の編集を妨げないため無視する
        // (終着番線の番線セレクトが無効化され、注記が表示される)。
        if (!aborted) setGtfsStops([]);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [apiClient]);

  // routes 読み込み後、全 route_id の駅順マスタを ControlPanel API からまとめて取得する。

  // 接続線遅延コンテンツの開始/終了駅ドロップダウンで利用する。
  // 取得に失敗した route_id は単にスキップする (ドロップダウンは空になるだけ)。
  useEffect(() => {
    let aborted = false;
    if (routes.length === 0) return;
    void (async () => {
      const entries = await Promise.all(
        routes.map(async (route) => {
          try {
            const data = await apiClient.getRouteStops(route.route_id);
            return [route.route_id, sortStopsBySequence(data.stops)] as const;
          } catch {
            // 駅順マスタ未登録 (404) 等は無視する
            return [route.route_id, [] as RouteStop[]] as const;
          }
        }),
      );
      if (aborted) return;
      setRouteStops(Object.fromEntries(entries));
    })();
    return () => {
      aborted = true;
    };
  }, [routes, apiClient]);

  // 選択中の線区/方面に対応する線形 GeoJSON を取得する。
  // - 線区/方面のいずれかが未選択 → null (= 線形を表示しない)
  // - 全線区モード → null (単一の線形を特定できないため)
  // - GeoJSON が存在しない (404 等) → null (= 同上)
  useEffect(() => {
    let aborted = false;
    if (isAllRoutes || !selectedRouteId || !selectedDirectionName) {
      setRouteShapePoints(null);
      return;
    }
    // route/direction 切替時は一旦クリアして表示の取り違えを防ぐ
    setRouteShapePoints(null);
    void (async () => {
      const data = await fetchRouteShapePoints(selectedRouteId, selectedDirectionName);
      if (!aborted) setRouteShapePoints(data);
    })();
    return () => {
      aborted = true;
    };
  }, [isAllRoutes, selectedRouteId, selectedDirectionName]);

  /**
   * 一覧取得。
   *
   * 線区が未選択のとき (= トップページの線区選択画面) は API を呼ばない。
   * 従来は全件 scan していたが、その状態で一覧を見る運用が無いため、
   * 初期表示の待ち時間と DynamoDB scan コストを削減する。
   *
   * ただしユーザーが明示的に「(全線区)」を選んだ場合 (= `ALL_ROUTES`) は、
   * `routeDirection` 無しで一覧を取得する (= 全件 scan)。
   * 意図した操作なので scan コストを受け入れる。
   *
   * 線区マスタ未読込 (`routes.length === 0`) のときも呼ばない。
   * `sanitizeSelection` はマスタ未読込時は検証をスキップするため、
   * URL に不正な線区が指定されていると一瞬だけ不正な routeDirection で
   * Query を投げてしまうのを防ぐ。
   */
  const reload = useCallback(async (): Promise<void> => {
    if (routes.length === 0 || !selectedRouteId) {
      // NOTE: 空配列を毎回新規生成して setState すると、`apiClient` (= reload) の
      // 参照が何らかの理由で毎レンダー変わる状況で無限ループになり得る。
      // 既に空なら state を触らないことで安全側に倒す。
      setAllPoints((prev) => (prev.length === 0 ? prev : []));
      setLoading(false);
      setErrorMessage(null);
      return;
    }
    setLoading(true);
    setErrorMessage(null);
    try {
      // 方面まで選択済みなら Query、線区のみ / 全線区なら scan +
      // (線区のみの場合は) クライアント側で絞り込み
      const res = await apiClient.list(selectedRouteDirection || undefined);
      setAllPoints(res.items);
    } catch (err) {
      setErrorMessage(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [apiClient, routes, selectedRouteId, selectedRouteDirection]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /**
   * 編集 / 新規作成モーダル表示中は、ヘッダータイトルからのトップ遷移をブロックする。
   * (誤クリックで入力内容が失われるのを防ぐ)
   */
  const { setBlocked } = useHomeNavigationGuard();
  useEffect(() => {
    setBlocked(formState !== null);
    return () => setBlocked(false);
  }, [formState, setBlocked]);

  const handleCreate = (): void => {
    if (!selectedRouteDirection) return;
    setFormState({
      mode: 'create',
      initialValue: {
        routeDirection: selectedRouteDirection,
        spotId: undefined,
        description: '',
        latitude: undefined,
        longitude: undefined,
        radius_meters: DEFAULT_RADIUS,
        broadcast_delay_seconds: DEFAULT_BROADCAST_DELAY,
        route_nickname: '',
        // voice_id 未設定は空文字 = サーバー側デフォルト (Takumi) にフォールバック。
        voice_id: '',
        // 要件 3・4: 新規作成時のチャイムは既定で「(なし)」= 鳴らさない (既存挙動と同一)。
        start_chime_id: '',
        end_chime_id: '',
        contents: [],
      },
    });
  };

  const handleEdit = (point: BroadcastPoint): void => {
    setFormState({
      mode: 'edit',
      initialValue: {
        routeDirection: point.routeDirection,
        spotId: point.spotId,
        description: point.description ?? '',
        latitude: point.latitude,
        longitude: point.longitude,
        radius_meters: point.radius_meters ?? DEFAULT_RADIUS,
        broadcast_delay_seconds: point.broadcast_delay_seconds ?? DEFAULT_BROADCAST_DELAY,
        // route_nickname は DynamoDB では未設定なら属性欠落。フォーム内では string で扱うため
        // 空文字を初期値としてセットする (保存時に handleFormSubmit で undefined 化)。
        route_nickname: point.route_nickname ?? '',
        // voice_id も同様に、DynamoDB 上で未設定なら属性欠落。空文字を初期値としてセットする
        // (保存時に handleFormSubmit で空文字→undefined に正規化)。
        voice_id: point.voice_id ?? '',
        // 要件 3・4: チャイム設定も DynamoDB 上で未設定なら属性欠落のため空文字で初期化する
        // (保存時に handleFormSubmit で空文字→undefined に正規化)。
        start_chime_id: point.start_chime_id ?? '',
        end_chime_id: point.end_chime_id ?? '',
        contents: extractContents(point),
      },
    });
  };

  /**
   * 編集モーダルから「コピーして新規作成」ボタンが押された時のハンドラ。
   *
   * モーダル内のコピー先選択ダイアログでユーザーが選んだ routeDirection を
   * 第 2 引数で受け取り、以下を初期値とする新規作成モーダルへ切り替える:
   *   - routeDirection: ユーザーが選択したコピー先 (元と異なる線区/方面も指定可)
   *   - 半径 / 放送遅延 / コンテンツ: そのまま引き継ぐ
   *   - spotId: undefined (新規採番させる)
   *   - 説明 / 緯度 / 経度: 空欄にしてユーザーに再入力させる
   *     (同一場所での重複登録を避けるため + 説明はコピー先の地点で書き直す前提)
   */
  const handleCopy = (values: BroadcastPointFormValues, routeDirection: string): void => {
    setFormState({
      mode: 'create',
      initialValue: {
        ...values,
        routeDirection,
        spotId: undefined,
        description: '',
        latitude: undefined,
        longitude: undefined,
      },
    });
  };

  /**
   * 削除ボタン押下時に呼ばれるハンドラ。
   * 確認ダイアログを表示するため対象を state に格納するだけで、実削除は
   * `executeDelete` (= ConfirmDialog の onConfirm) で行う。
   */
  const handleDelete = (point: BroadcastPoint): void => {
    setDeleteTarget(point);
  };

  /**
   * 削除確認ダイアログで OK が押されたときの実削除処理。
   */
  const executeDelete = async (): Promise<void> => {
    const point = deleteTarget;
    setDeleteTarget(null);
    if (!point) return;
    try {
      await apiClient.remove(point.spotId, point.routeDirection);
      await reload();
    } catch (err) {
      setErrorMessage(formatApiError(err));
    }
  };

  const handleFormSubmit = async (values: BroadcastPointFormValues): Promise<void> => {
    // route_nickname はフォーム内で string 保持しているので、
    // trim 後空なら未設定扱いとして API payload から除外する (Lambda 側の
    // ALLOWED_SPOT_FIELDS 正規化で空文字を弾くため送っても消えるが、明示的に落とす)。
    const nicknameTrimmed = (values.route_nickname ?? '').trim();
    // voice_id も同じく空文字は未設定扱い (= サーバー側デフォルト Takumi にフォールバック)。
    const voiceIdTrimmed = (values.voice_id ?? '').trim();
    // 要件 3・4: 放送開始 / 終了チャイム。
    // 「(なし)」選択 = 空文字は「鳴らさない」を意味する。
    //   - 新規作成時: 属性そのものを送らない (undefined) ことで DynamoDB に保存しない。
    //   - 更新時: 空文字を送ると Lambda 側 (_coerce_chime_id) が item から除去するため、
    //     PUT が put_item (全置換) である性質と合わせて設定解除として機能する。
    // どちらのケースも `undefined` でよい (更新は全置換なので属性が消える)。
    const startChimeTrimmed = (values.start_chime_id ?? '').trim();
    const endChimeTrimmed = (values.end_chime_id ?? '').trim();
    const payload = {
      routeDirection: values.routeDirection,
      description: values.description,
      latitude: values.latitude,
      longitude: values.longitude,
      radius_meters: values.radius_meters,
      broadcast_delay_seconds: values.broadcast_delay_seconds,
      route_nickname: nicknameTrimmed === '' ? undefined : nicknameTrimmed,
      voice_id: voiceIdTrimmed === '' ? undefined : voiceIdTrimmed,
      start_chime_id: startChimeTrimmed === '' ? undefined : startChimeTrimmed,
      end_chime_id: endChimeTrimmed === '' ? undefined : endChimeTrimmed,
      contents: values.contents,
    };
    if (formState?.mode === 'edit' && values.spotId) {
      await apiClient.update(values.spotId, payload);
    } else {
      await apiClient.create(payload);
    }
    setFormState(null);
    await reload();
  };

  const handleImport = async (
    importItems: BroadcastPoint[],
  ): Promise<{ success: number; failures: string[] }> => {
    let success = 0;
    const failures: string[] = [];
    for (const item of importItems) {
      // Import 側でも空文字は未設定扱いに正規化する (parseImportJson で trim 空は
      // undefined になっているはずだが、二重防御)。
      const nicknameTrimmed = (item.route_nickname ?? '').trim();
      const voiceIdTrimmed = (item.voice_id ?? '').trim();
      // 要件 3・4: チャイム設定も空文字は未設定扱い (鳴らさない) に正規化する。
      const startChimeTrimmed = (item.start_chime_id ?? '').trim();
      const endChimeTrimmed = (item.end_chime_id ?? '').trim();
      const payload = {
        routeDirection: item.routeDirection,
        description: item.description,
        latitude: item.latitude,
        longitude: item.longitude,
        radius_meters: item.radius_meters,
        broadcast_delay_seconds: item.broadcast_delay_seconds,
        route_nickname: nicknameTrimmed === '' ? undefined : nicknameTrimmed,
        voice_id: voiceIdTrimmed === '' ? undefined : voiceIdTrimmed,
        start_chime_id: startChimeTrimmed === '' ? undefined : startChimeTrimmed,
        end_chime_id: endChimeTrimmed === '' ? undefined : endChimeTrimmed,
        contents: item.contents,
      };

      try {
        if (item.spotId) {
          await apiClient.update(item.spotId, payload);
        } else {
          await apiClient.create(payload);
        }
        success++;
      } catch (err) {
        failures.push(`${item.routeDirection}/${item.spotId || '(new)'}: ${formatApiError(err)}`);
      }
    }
    await reload();
    return { success, failures };
  };

  /**
   * 地図を表示するか。
   * 線区が選択されている (全線区モードも含む) ときに表示する。
   * 全線区モードでは線形ラインは描かず、放送地点ピンのみを重ねて表示する。
   */
  const showOverviewMap = selectedRouteId !== '';

  /**
   * 地図セクション (SplitPane の右ペインに配置する)。
   * 線区が選択されているときのみ描画される。
   */
  const mapSection = (
    <section className="section points-layout__map" data-testid="map-section">
      <div className="overview-map">
        <BroadcastPointsMap
          otherPoints={filteredPoints}
          routeShapePoints={routeShapePoints}
          onPointClick={handleEdit}
          focusPoint={focusPoint}
        />
      </div>
    </section>
  );

  /**
   * 放送地点一覧セクション (SplitPane の左ペインに配置する)。
   * 線区未選択で地図が無い場合は、このセクションのみ全幅で表示する。
   */
  const listSection = (
    <section className="section points-layout__list" data-testid="list-section">
      <h2>
        放送地点一覧{' '}
        {loading
          ? '(読み込み中...)'
          : sortedFilteredPoints.length === filteredPoints.length
            ? `(${filteredPoints.length} 件)`
            : `(${sortedFilteredPoints.length} / ${filteredPoints.length} 件)`}
      </h2>
      {errorMessage ? <div className="error-message">{errorMessage}</div> : null}
      <div className="sort-filter-bar">
        <div className="sort-filter-group">
          <span className="sort-filter-label">ソート</span>
          <label className="sort-filter-radio">
            <input
              type="radio"
              name="sortOrder"
              value="station_order"
              checked={sortOrder === 'station_order'}
              onChange={() => setSortOrder('station_order')}
            />
            駅並び順
          </label>
          <label className="sort-filter-radio">
            <input
              type="radio"
              name="sortOrder"
              value="created_at"
              checked={sortOrder === 'created_at'}
              onChange={() => setSortOrder('created_at')}
            />
            登録日時順
          </label>
        </div>
        <div className="sort-filter-group">
          <label className="sort-filter-select-label" htmlFor="content-filter">
            <span className="sort-filter-label">コンテンツ種別</span>
            <select
              id="content-filter"
              value={contentFilter}
              onChange={(e) => setContentFilter(e.target.value as ContentFilterValue)}
            >
              <option value="">すべて</option>
              <option value="self_train_delay">自列車遅延</option>
              <option value="connecting_line_delay">他線区遅延</option>
              <option value="free_text">任意テキスト</option>
            </select>
          </label>
        </div>
      </div>
      <BroadcastPointsTable
        points={sortedFilteredPoints}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onRowClick={showOverviewMap ? setFocusPoint : undefined}
      />
    </section>
  );

  /**
   * 左カラム: 操作パネル (線区/方面セレクタ + アクション) + 放送地点一覧。
   *
   * 画面全体を活かすため、操作パネルは上部に固定し、一覧テーブルだけを
   * 残り領域でスクロールさせる (`.points-layout` / `.points-layout__list`)。
   */
  const leftColumn = (
    <div className="points-layout">
      {/*
        操作パネル。
        以前は「放送新規設定」という見出しを付けていたが、意味が伝わらないため削除した。
        また「選択中の線区/方面バッジ」も廃止した (線区/方面ドロップダウンの現在値と
        同じ情報が 2 箇所に出て冗長だったため)。線区カラーは RouteSelector 内で
        ドロップダウン下の路線ライン + パネル左端のアクセントラインとして表示する。
      */}
      <section className="section points-layout__controls">
        <RouteSelector
          routes={routes}
          selectedRouteId={selectedRouteId}
          selectedDirectionName={selectedDirectionName}
          onChange={(nextRouteId, nextDirectionName) => {
            setSelection(nextRouteId, nextDirectionName);
          }}
          onCreate={handleCreate}
          onReload={() => void reload()}
          onBackToTop={clearSelection}
          extraActions={<ImportExportControl items={filteredPoints} onImport={handleImport} />}
        />
      </section>
      {listSection}
    </div>
  );

  /**
   * 線区未選択 (= トップページ) のときは、テーブルの代わりに線区選択ランディングを表示する。
   * ランディングは 1 段階選択なので、方面ボタン 1 クリックで `setSelection` により
   * 選択が確定し、そのまま一覧 + 地図画面へ遷移する。
   *
   * NOTE: 判定は「空文字かどうか」で行う。`ALL_ROUTES` (= 全線区) は
   * ユーザーが明示的に選んだ状態なのでランディングへ戻してはいけない。
   */
  if (selectedRouteId === '') {
    return (
      <>
        <RouteLandingSelector
          routes={routes}
          loading={routes.length === 0 && errorMessage === null}
          onSelect={(nextRouteId, nextDirectionName) =>
            setSelection(nextRouteId, nextDirectionName)
          }
        />
        {errorMessage ? (
          <div className="error-message" style={{ padding: '0 24px 16px' }}>
            {errorMessage}
          </div>
        ) : null}
      </>
    );
  }

  return (
    <>
      {/*
        受け入れ条件「コンテンツ一覧と地図が横並びで表示されること」に対応して、
        左カラム (操作パネル + 一覧) と地図セクションを `SplitPane` で横並びにする。
        分割バーはマウスドラッグ (またはキーボード ←→) で領域配分を変更できる。

        `fullHeight` により SplitPane はメイン領域 (ヘッダー以下すべて) の高さを
        占め、地図はウィンドウ下端まで広がる。

        線区が未選択のときは上の early return でランディング画面を返すため、
        ここでは常に地図付きの 2 カラムになる (showOverviewMap は防御的判定)。
      */}
      {showOverviewMap ? (
        <SplitPane
          testId="points-map-split"
          // 一覧側 (左) を少し広めにして、地図 (右) と並べて見比べやすくする
          initialLeftRatio={55}
          fullHeight
          splitterAriaLabel="一覧と地図の領域配分を変更"
          left={leftColumn}
          right={mapSection}
        />
      ) : (
        leftColumn
      )}

      {formState ? (
        <BroadcastPointForm
          mode={formState.mode}
          initialValue={formState.initialValue}
          otherPoints={filteredPoints.filter((p) => p.spotId !== formState.initialValue.spotId)}
          routeShapePoints={routeShapePoints}
          // 親が `routeShapePoints` をどの routeDirection 用に取得したかを明示する。
          // 「コピーして新規作成」でコピー元と異なる線区/方面が選ばれた際、
          // フォーム側でこの値と `initialValue.routeDirection` のズレを検知し、
          // 親値の代わりにフォーム側で自前 fetch した線形を使って地図を描画させる。
          routeShapePointsRouteDirection={selectedRouteDirection}
          routes={routes}
          routeStops={routeStops}
          // 接続線遅延の「運行情報の参照元（アイステイションズ路線）」複数選択用マスタ。
          // ATOS 線区マスタ (routes) とは独立した別系統で、連動させない。
          istationsLines={istationsLines}
          // 任意テキストの放送条件「終着番線」の番線ドロップダウン候補 (GTFS stops 全件)。
          // route_id で絞り込めないため、マウント時に 1 回だけ取得した全件を渡す。
          gtfsStops={gtfsStops}
          // 要件 3・4: 放送開始 / 終了チャイムの選択肢 (チャイムマスタ) と試聴ハンドラ。
          chimes={chimes}
          onPreviewChime={async (chimeId) => {
            const { content_type, content_base64 } = await chimesClient.getAudio(chimeId);
            return base64ToAudioUrl(content_base64, content_type);
          }}
          onUploadAudio={async (file) => {
            const { audio_key } = await apiClient.uploadAudio(file);
            return audio_key;
          }}
          onPreviewSsml={async (input) => {
            const { content_type, content_base64 } = await apiClient.previewAudio({
              text: input.text,
              ssml: input.ssml,
              isSsml: input.isSsml,
              // モーダル内で選択された Polly VoiceId (未選択は undefined) をそのまま
              // プレビュー API に渡すことで、実放送と同じ声色で試聴できるようにする。
              voiceId: input.voiceId,
            });
            return base64ToAudioUrl(content_base64, content_type);
          }}
          onSubmit={handleFormSubmit}
          onCopy={handleCopy}
          onClose={() => setFormState(null)}
        />
      ) : null}
      {/* 削除確認ダイアログ (window.confirm の代替) */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="放送地点の削除"
        message={`放送地点 "${deleteTarget?.description ?? '(名称未設定)'}" を削除します。よろしいですか？`}
        confirmLabel="削除"
        destructive
        onConfirm={() => void executeDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}

/** API クライアントの例外を画面表示用文字列に整形する */
function formatApiError(err: unknown): string {
  if (err instanceof ApiError) {
    return `[HTTP ${err.status}] ${err.message}`;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return '予期しないエラーが発生しました';
}

/**
 * 放送地点の座標から「最近傍区間の開始駅 stop_sequence」を返す。
 * 駅順マスタが空、または座標が未設定の場合は Infinity を返して末尾に送る。
 */
function getStationSequence(point: BroadcastPoint, stops: RouteStop[]): number {
  if (
    stops.length < 2 ||
    typeof point.latitude !== 'number' ||
    typeof point.longitude !== 'number'
  ) {
    return Infinity;
  }
  const segment = findStationSegment(stops, point.latitude, point.longitude);
  return segment?.startStop.stop_sequence ?? Infinity;
}
